import { FC, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Stack, Typography } from '@mui/material';
import {
  BasketAddRemoveEvent,
  BasketCheckoutEvent,
  CategoryViewShopperEvent,
  ProductViewShopperEvent,
  SearchShopperEvent,
  ShopperEventCategory,
  ShopperEventType,
  useGetShopperEvents,
  useVsWebsocket,
} from '@vs-online-only-app/state';
import { ShopperEventsModal } from '../shopper-profile/ShopperEventsModal';
import { useSelectedHeroConversation } from '../../hooks/useSelectedHeroConversation';
import ShopperEventsCategoryCard from '../shopper-profile/ShopperEventsCategoryCard';
import SelectedConversationShopperLanguageAndLocation from './SelectedConversationShopperLanguageAndLocation';

const Wrapper = styled(Stack)`
  gap: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.palette.background.default};
  padding: ${({ theme }) => theme.spacing(3, 3, 0)};
  overflow: auto;
`;

const ShopperEventsWrapper = styled(Stack)`
  overflow: auto;
  height: 100%;
  padding-bottom: ${({ theme }) => theme.spacing(4)};
  gap: ${({ theme }) => theme.spacing(2)};
`;

export type ShopperEventsByCategory = {
  [ShopperEventCategory.PURCHASED]: BasketCheckoutEvent[];
  [ShopperEventCategory.RECENTLY_VIEWED]: (
    | ProductViewShopperEvent
    | CategoryViewShopperEvent
    | SearchShopperEvent
  )[];
  [ShopperEventCategory.BASKET_ADDED_REMOVED]: {
    added: BasketAddRemoveEvent[];
    removed: BasketAddRemoveEvent[];
  };
};

const SelectedConversationShopperProfile: FC = () => {
  const { listenToShopperEvents, isConnected } = useVsWebsocket();
  const conversation = useSelectedHeroConversation();

  const { data: shopperEvents } = useGetShopperEvents(
    conversation?.conversationStarter.id
  );

  useEffect(() => {
    if (isConnected && conversation?.currentAgent) {
      listenToShopperEvents?.({
        shopperId: conversation.conversationStarter.id,
        retailerId: conversation.currentAgent.retailer,
      });
    }
  }, [conversation, isConnected]);

  const shopperEventsByCategory: ShopperEventsByCategory = useMemo(
    () => ({
      [ShopperEventCategory.RECENTLY_VIEWED]: shopperEvents?.filter((event) =>
        [
          ShopperEventType.ProductView,
          ShopperEventType.CategoryView,
          ShopperEventType.Search,
        ].includes(event.type)
      ) as
        | (
            | ProductViewShopperEvent
            | CategoryViewShopperEvent
            | SearchShopperEvent
          )[],
      [ShopperEventCategory.BASKET_ADDED_REMOVED]: {
        added: shopperEvents?.filter(
          (event) => event.type === ShopperEventType.BasketAdd
        ) as BasketAddRemoveEvent[],
        removed: shopperEvents?.filter(
          (event) => event.type === ShopperEventType.BasketDelete
        ) as BasketAddRemoveEvent[],
      },
      [ShopperEventCategory.PURCHASED]: shopperEvents?.filter(
        (event) => event.type === ShopperEventType.BasketCheckout
      ) as BasketCheckoutEvent[],
    }),
    [shopperEvents]
  );

  if (!conversation || !shopperEvents) {
    return null;
  }

  return (
    <>
      <Wrapper>
        <Typography variant="h5">
          {conversation.conversationStarter.firstName}’s profile
        </Typography>
        <ShopperEventsWrapper>
          <SelectedConversationShopperLanguageAndLocation />
          <ShopperEventsCategoryCard
            category={ShopperEventCategory.RECENTLY_VIEWED}
            events={shopperEventsByCategory}
          />
          <ShopperEventsCategoryCard
            category={ShopperEventCategory.BASKET_ADDED_REMOVED}
            events={shopperEventsByCategory}
          />
          <ShopperEventsCategoryCard
            category={ShopperEventCategory.PURCHASED}
            events={shopperEventsByCategory}
          />
        </ShopperEventsWrapper>
      </Wrapper>
      <ShopperEventsModal events={shopperEventsByCategory} />
    </>
  );
};

export default SelectedConversationShopperProfile;
